import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Faders, Plus, Trash, Lightning, Power, PlayCircle, Copy } from "@phosphor-icons/react";

const FREQS = ["Monthly", "Quarterly", "Annually"];
const ruleSummary = (r) => {
  const m = r.match || {}, t = r.treatment || {};
  const parts = [];
  if (m.contact_contains) parts.push(`Contact ~ "${m.contact_contains}"`);
  if (m.account_code) parts.push(`Code ${m.account_code}`);
  if (m.description_contains) parts.push(`Desc ~ "${m.description_contains}"`);
  parts.push(`${t.frequency || "Monthly"} · ${t.term_months || 12}m`);
  return parts.join(" · ");
};
const empty = {
  name: "", match: { contact_contains: "", account_code: "", description_contains: "" },
  treatment: { frequency: "Monthly", term_months: 12, pl_account_code: "", release_basis: "monthly" },
  auto_apply: false, enabled: true,
};

export default function TreatmentRules({ onApplied, standalone }) {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(Boolean(standalone));
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(empty);
  const [applying, setApplying] = useState(false);
  const [otherOrgs, setOtherOrgs] = useState([]);
  const [activeName, setActiveName] = useState("this entity");
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySrc, setCopySrc] = useState("");
  const [srcRules, setSrcRules] = useState([]);
  const [picked, setPicked] = useState({});
  const [loadingSrc, setLoadingSrc] = useState(false);
  const [copying, setCopying] = useState(false);

  const load = useCallback(() => api.get("/rules").then(({ data }) => setRules(data.rules || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data.settings)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/xero/status").then(({ data }) => {
      const orgs = data.organisations || [];
      setOtherOrgs(orgs.filter((o) => o.selected && !o.active));
      setActiveName((orgs.find((o) => o.active) || {}).tenant_name || "this entity");
    }).catch(() => {});
  }, []);

  const existingNames = new Set(rules.map((r) => r.name));
  const selectedCount = Object.values(picked).filter(Boolean).length;

  // BS codes tagged as Prepayment / Accrual / Deferred Income for this entity,
  // intersected with the actual chart of accounts (drops orphaned/legacy codes).
  const typeCodeSet = new Set([
    ...(settings?.prepayment_codes || []),
    ...(settings?.accrual_codes || []),
    ...(settings?.deferred_income_codes || []),
  ]);
  const accountCodeOptions = accounts.filter((a) => typeCodeSet.has(a.code));
  // Finding 4: valid P&L release destinations only — revenue/expense accounts, never a bank,
  // receivable, payable or equity account.
  const isPL = (a) => {
    const cls = (a.class || "").toUpperCase();
    const typ = (a.type || "").toUpperCase();
    return typ !== "BANK" && (cls === "REVENUE" || cls === "EXPENSE");
  };
  const plCodeOptions = accounts.filter(isPL);

  const pickSource = async (tid) => {
    setCopySrc(tid); setSrcRules([]); setPicked({});
    if (!tid) return;
    setLoadingSrc(true);
    try {
      const { data } = await api.get(`/rules/by-entity/${tid}`);
      const rs = data.rules || [];
      setSrcRules(rs);
      const init = {};
      rs.forEach((r) => { if (!existingNames.has(r.name)) init[r.id] = true; });
      setPicked(init);
    } catch { toast.error("Could not load that entity's rules"); }
    finally { setLoadingSrc(false); }
  };

  const closeCopy = () => { setCopyOpen(false); setCopySrc(""); setSrcRules([]); setPicked({}); };

  const doCopy = async () => {
    const ids = Object.keys(picked).filter((id) => picked[id]);
    if (!ids.length) return;
    setCopying(true);
    try {
      const { data } = await api.post("/rules/copy-from", { source_tenant_id: copySrc, rule_ids: ids });
      if (data.copied) toast.success(`Copied ${data.copied} rule(s)${data.skipped ? ` · ${data.skipped} skipped (duplicate names)` : ""}`);
      else toast.info("Nothing copied");
      closeCopy(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not copy rules"); }
    finally { setCopying(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the rule a name");
    const m = form.match;
    if (!m.contact_contains && !m.account_code && !m.description_contains) return toast.error("Add at least one match criterion");
    if (!form.treatment.pl_account_code) return toast.error("Set the P&L release code");
    try {
      await api.post("/rules", form);
      toast.success("Rule saved");
      setForm(empty); setAdding(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save rule"); }
  };

  const toggle = async (r, field) => {
    await api.patch(`/rules/${r.id}`, { [field]: !r[field] });
    load();
  };

  const del = async (r) => { await api.delete(`/rules/${r.id}`); load(); };

  const applyNow = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/rules/apply");
      if (data.count > 0) { toast.success(`Scheduled ${data.count} item(s) via rules`); onApplied?.(); }
      else toast.info("No unscheduled items matched your rules");
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setApplying(false); }
  };

  const F = (path, val) => {
    const [a, b] = path.split(".");
    setForm((f) => (b ? { ...f, [a]: { ...f[a], [b]: val } } : { ...f, [a]: val }));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl mb-5 shadow-card" data-testid="treatment-rules-panel">
      <div role="button" tabIndex={0} onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === "Enter") setOpen(!open); }}
        className="w-full flex items-center justify-between px-5 py-3.5 cursor-pointer">
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-lg bg-navy/5 text-navy flex items-center justify-center"><Faders size={16} /></span>
          <div className="text-left">
            <div className="font-heading font-bold text-navy text-base tracking-tight">Treatment rules</div>
            <div className="text-xs text-navy/70">{rules.length} rule{rules.length === 1 ? "" : "s"} — auto-apply a saved treatment to matching items</div>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <button onClick={applyNow} disabled={applying} data-testid="apply-rules-btn"
            className="inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-lg border border-orange/40 text-orangeink hover:bg-orange/5 transition-colors disabled:opacity-60">
            <PlayCircle size={16} weight="fill" /> {applying ? "Applying…" : "Apply rules now"}
          </button>
          <span className="text-xs text-navy/65">{open ? "Hide" : "Manage"}</span>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-3">
          {otherOrgs.length > 0 && (
            <button onClick={() => setCopyOpen(true)} data-testid="open-copy-rules"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors">
              <Copy size={15} /> Copy rules from another entity
            </button>
          )}
          {rules.map((r) => (
            <div key={r.id} className="flex items-center gap-3 flex-wrap py-2 border-b border-navy/5 last:border-0" data-testid={`rule-row-${r.id}`}>
              <button onClick={() => toggle(r, "enabled")} title={r.enabled ? "Enabled" : "Disabled"} data-testid={`rule-enable-${r.id}`}
                className={`w-8 h-8 rounded-lg flex items-center justify-center border ${r.enabled ? "bg-positive/10 text-positive border-positive/30" : "bg-navy/5 text-navy/30 border-slate-200"}`}>
                <Power size={15} weight="bold" />
              </button>
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold text-navy text-sm flex items-center gap-2">
                  {r.name}
                  {r.auto_apply && <span className="text-[0.58rem] uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-orange/10 text-orangeink border border-orange/30 inline-flex items-center gap-1"><Lightning size={10} weight="fill" />auto</span>}
                </div>
                <div className="text-xs text-navy/70 mt-0.5">
                  {[r.match.account_code && `code ${r.match.account_code}`, r.match.contact_contains && `contact ~ "${r.match.contact_contains}"`, r.match.description_contains && `desc ~ "${r.match.description_contains}"`].filter(Boolean).join(" · ")}
                  {" → "}{r.treatment.frequency}, {r.treatment.term_months}m → {r.treatment.pl_account_code}
                </div>
              </div>
              <button onClick={() => toggle(r, "auto_apply")} data-testid={`rule-auto-${r.id}`}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-navy/70 hover:bg-navy/5">{r.auto_apply ? "Auto on" : "Auto off"}</button>
              <button onClick={() => del(r)} data-testid={`rule-delete-${r.id}`}
                className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 text-navy/65 hover:text-red-600 hover:border-red-300"><Trash size={15} /></button>
            </div>
          ))}

          {!adding ? (
            <button onClick={() => setAdding(true)} data-testid="add-rule-btn"
              className="inline-flex items-center gap-1.5 text-sm text-orangeink font-medium pt-1"><Plus size={15} weight="bold" /> Add rule</button>
          ) : (
            <div className="bg-navy/[0.02] border border-slate-100 rounded-lg p-4 space-y-3" data-testid="add-rule-form">
              <input value={form.name} onChange={(e) => F("name", e.target.value)} placeholder="Rule name (e.g. Annual insurance prepayment)"
                data-testid="rule-name-input" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-orange" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Lbl t="Contact contains"><input value={form.match.contact_contains} onChange={(e) => F("match.contact_contains", e.target.value)} data-testid="rule-contact-input" className={inp} /></Lbl>
                <Lbl t="Account code">
                  <select value={form.match.account_code} onChange={(e) => F("match.account_code", e.target.value)} data-testid="rule-code-input" className={inp}>
                    <option value="">Any of my classified codes</option>
                    {accountCodeOptions.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                  </select>
                </Lbl>
                <Lbl t="Description contains"><input value={form.match.description_contains} onChange={(e) => F("match.description_contains", e.target.value)} data-testid="rule-desc-input" className={inp} /></Lbl>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <Lbl t="Frequency"><select value={form.treatment.frequency} onChange={(e) => F("treatment.frequency", e.target.value)} className={inp}>{FREQS.map((f) => <option key={f}>{f}</option>)}</select></Lbl>
                <Lbl t="Term (months)"><input type="number" min="1" value={form.treatment.term_months} onChange={(e) => F("treatment.term_months", parseInt(e.target.value || "1", 10))} className={inp} /></Lbl>
                <Lbl t="Release to P&L code">
                  <select value={form.treatment.pl_account_code} onChange={(e) => F("treatment.pl_account_code", e.target.value)} data-testid="rule-pl-input" className={inp}>
                    <option value="">Select a P&L code…</option>
                    {plCodeOptions.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                  </select>
                </Lbl>
              </div>
              <label className="flex items-center gap-2 text-sm text-navy/70">
                <input type="checkbox" checked={form.auto_apply} onChange={(e) => F("auto_apply", e.target.checked)} data-testid="rule-autoapply-checkbox" /> Auto-apply on import
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => { setAdding(false); setForm(empty); }} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
                <button onClick={save} data-testid="save-rule-btn" className="px-5 py-2 text-sm font-medium rounded-lg bg-navy text-white hover:bg-orange transition-colors">Save rule</button>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={copyOpen} onOpenChange={(o) => !o && closeCopy()}>
        <DialogContent className="max-w-lg rounded-2xl border-slate-200" data-testid="copy-rules-dialog">
          <DialogHeader>
            <DialogTitle>Copy treatment rules</DialogTitle>
            <DialogDescription>Pick the rules to copy into <span className="font-semibold text-navy">{activeName}</span>. Copies are independent and belong only to this entity.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <select value={copySrc} onChange={(e) => pickSource(e.target.value)} data-testid="copy-source-select"
              className="w-full text-sm bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-orange">
              <option value="">Select an entity to copy from…</option>
              {otherOrgs.map((o) => <option key={o.tenant_id} value={o.tenant_id}>{o.tenant_name}</option>)}
            </select>
            {loadingSrc && <div className="text-sm text-slate-600 py-6 text-center font-mono tracking-widest">LOADING…</div>}
            {copySrc && !loadingSrc && srcRules.length === 0 && <div className="text-sm text-slate-600 py-6 text-center">That entity has no treatment rules.</div>}
            {srcRules.length > 0 && (
              <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="copy-rules-list">
                {srcRules.map((r) => {
                  const dup = existingNames.has(r.name);
                  return (
                    <label key={r.id} data-testid={`copy-rule-${r.id}`}
                      className={`flex items-start gap-3 px-3 py-2.5 ${dup ? "opacity-50" : "cursor-pointer hover:bg-slate-50"}`}>
                      <input type="checkbox" disabled={dup} checked={!!picked[r.id]} onChange={() => setPicked((p) => ({ ...p, [r.id]: !p[r.id] }))} className="mt-1" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-navy">{r.name} {dup && <span className="text-[0.62rem] text-slate-600 font-normal">· already here</span>}</div>
                        <div className="text-xs text-slate-600">{ruleSummary(r)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={closeCopy} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-slate-50">Cancel</button>
            <button onClick={doCopy} disabled={!selectedCount || copying} data-testid="copy-selected-btn"
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-lg bg-navy text-white hover:bg-orange transition-colors disabled:opacity-50">
              <Copy size={14} weight="bold" /> {copying ? "Copying…" : `Copy${selectedCount ? ` ${selectedCount}` : ""} selected`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inp = "w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-orange";
const Lbl = ({ t, children }) => (<div><div className="overline text-navy/65 mb-1">{t}</div>{children}</div>);
